import { Currency, ResolverTag } from '@ps/web-core-enums';
import type { TranslationTag } from '@ps/web-core-intl';
import { type Error } from '@ps/web-shared-validators';
import React from 'react';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import ResolverLink from '@swf/components/ResolverLink/ResolverLink';

import type { MaxLimitData, Rate, UsedDaysLimitHitTypes } from '../types/Cashier';
import { DaysLimitHitTypes, DepositLimitHitTypes } from '../types/Cashier';
import { convertAmountByRate, getConversionRate, isZeroExponentCurrency } from '../utils/cashier';

import {
  TXTWCL_SorryX_your_deposit_request_ha_ELL,
  TXTWCL_There_is_a_limit_of_N0_for_thi_ELL,
  TXTWCL_This_is_because_the_maximum_li_ELL,
  TXTWCL_This_is_because_the_maximum_li_ELL_1,
  TXTWCL_This_is_because_the_maximum_li_ELL_2,
  TXTWCL_This_is_because_you_can_only_d_ELL,
  TXTWCL_This_is_because_you_can_only_d_ELL_1,
  TXTWCL_This_is_because_you_can_only_d_ELL_2,
  TXTWCL_To_find_out_moreX_please_visit_ELL,
  TXTWCL_Visit_our_Help_Center_to_find__ELL,
  TXTWCL_You_can_deposit_up_to_N0_befor_ELL,
  TXTWCL_You_can_deposit_up_to_N0_befor_ELL_1,
  TXTWCL_You_can_only_deposit_another_N_ELL,
  TXTWCL_You_have_N0_left_on_your_Daily_ELL_1,
  TXTWCL_You_have_N0_left_on_your_Month_ELL_1,
  TXTWCL_You_have_N0_left_on_your_Staff_ELL,
  TXTWCL_You_have_N0_remaining_of_your__ELL,
  TXTWCL_You_have_now_reached_this_limi_ELL,
  TXTWCL_You_have_used_all_your_Spend_B_ELL,
  TXTWCL_Your_Spend_Budget_resets_on_th_ELL,
  TXTWCL__Please_enter_a_lower_amountX
} from './maxLimit.messages.json';

const TXTWCL_You_have_N0_remaining_of_your__ELL_spain = {
  id: 'text',
  defaultMessage: 'You have {0} left of your deposit limit.',
  track: 'text'
};

const TXTWCL_You_have_reached_spain = {
  id: 'text',
  defaultMessage: 'You have reached your deposit limit.',
  track: 'text'
};

const depositLimitMessages = {
  [DepositLimitHitTypes.TempAccountLimit]: TXTWCL_You_can_deposit_up_to_N0_befor_ELL,
  [DepositLimitHitTypes.StaffLimit]: TXTWCL_You_have_N0_left_on_your_Staff_ELL,
  [DepositLimitHitTypes.UkSpendBudgetLimit]: TXTWCL_You_have_N0_remaining_of_your__ELL,
  [DepositLimitHitTypes.SpainDepositLimit]: TXTWCL_You_have_N0_remaining_of_your__ELL_spain
};

const setLimitsMessage = (remainingLimit: number, daysLimitHitType: UsedDaysLimitHitTypes) => {
  const limitsMessage: Record<UsedDaysLimitHitTypes, TranslationTag> = {
    [DaysLimitHitTypes.Daily]: remainingLimit
      ? TXTWCL_This_is_because_the_maximum_li_ELL
      : TXTWCL_This_is_because_you_can_only_d_ELL,
    [DaysLimitHitTypes.Weekly]: remainingLimit
      ? TXTWCL_This_is_because_the_maximum_li_ELL_2
      : TXTWCL_This_is_because_you_can_only_d_ELL_1,
    [DaysLimitHitTypes.Monthly]: remainingLimit
      ? TXTWCL_This_is_because_the_maximum_li_ELL_1
      : TXTWCL_This_is_because_you_can_only_d_ELL_2
  };
  return limitsMessage[daysLimitHitType];
};

const getLimits = ({ day, week, month }: MaxLimitData, daysLimitHitType: UsedDaysLimitHitTypes) => {
  const limit = {
    [DaysLimitHitTypes.Daily]: day,
    [DaysLimitHitTypes.Weekly]: week,
    [DaysLimitHitTypes.Monthly]: month
  };

  return limit[daysLimitHitType];
};

export const createMaxLimitValidator =
  (
    balanceCurrency = Currency.USDollar,
    daysLimitHitType: UsedDaysLimitHitTypes,
    depositLimitHitType: DepositLimitHitTypes,
    maxLimitData: MaxLimitData,
    rates: Rate[],
    remainingLimit: number
  ) =>
  (value: number, allValues: { deposit: { currency: Currency } }): Error => {
    const {
      deposit: { currency }
    } = allValues;

    const remainingLimitDollarAmount = remainingLimit / 100;
    const { transaction } = maxLimitData;
    const currencyRate = getConversionRate(rates, currency);
    const convertedAmount = convertAmountByRate(Number(value), currencyRate, currency);
    const isBalanceCurrencyZeroExponent = isZeroExponentCurrency(balanceCurrency);
    const isSelectedCurrencyZeroExponent = isZeroExponentCurrency(currency);

    const valid = () => {
      const isValidForSelfImposed =
        depositLimitHitType === DepositLimitHitTypes.SelfImposedLimit &&
        convertedAmount <= remainingLimitDollarAmount &&
        convertedAmount <= transaction;

      const isValidForPaysystem =
        depositLimitHitType !== DepositLimitHitTypes.SelfImposedLimit &&
        !(
          remainingLimitDollarAmount < transaction && convertedAmount > remainingLimitDollarAmount
        ) &&
        convertedAmount <= transaction;

      const isValidForUKSpendBudget =
        depositLimitHitType === DepositLimitHitTypes.UkSpendBudgetLimit &&
        convertedAmount < remainingLimitDollarAmount;

      if (
        isNaN(value) ||
        (remainingLimitDollarAmount > 0 &&
          (isValidForSelfImposed || isValidForPaysystem || isValidForUKSpendBudget))
      ) {
        return true;
      }
      return false;
    };

    const message = () => {
      if (remainingLimitDollarAmount < transaction) {
        const maxLimitWithCurrency = (
          <CurrencyAmount
            value={convertAmountByRate(
              Number(remainingLimitDollarAmount),
              currencyRate,
              currency,
              true
            )}
            isZeroExponentCurrency={isSelectedCurrencyZeroExponent}
            currency={currency}
            dollarValue
          />
        );
        if (depositLimitHitType === DepositLimitHitTypes.NetDepositLimit) {
          return [
            {
              values: {
                0: maxLimitWithCurrency
              },
              ...(daysLimitHitType === DaysLimitHitTypes.Daily
                ? TXTWCL_You_have_N0_left_on_your_Daily_ELL_1
                : TXTWCL_You_have_N0_left_on_your_Month_ELL_1)
            }
          ];
        }
        if (depositLimitHitType === DepositLimitHitTypes.SelfImposedLimit) {
          return [
            {
              values: {
                0: maxLimitWithCurrency
              },
              ...TXTWCL_You_can_deposit_up_to_N0_befor_ELL_1
            },
            { ...TXTWCL__Please_enter_a_lower_amountX }
          ];
        }
        if (depositLimitHitType === DepositLimitHitTypes.PaysystemLimit) {
          if (remainingLimitDollarAmount === 0) {
            return [
              { ...TXTWCL_SorryX_your_deposit_request_ha_ELL },
              {
                ...setLimitsMessage(remainingLimitDollarAmount, daysLimitHitType),
                values: {
                  0: (
                    <CurrencyAmount
                      value={getLimits(maxLimitData, daysLimitHitType)}
                      isZeroExponentCurrency={isBalanceCurrencyZeroExponent}
                      currency={balanceCurrency}
                      dollarValue
                    />
                  )
                }
              },
              { ...TXTWCL_You_have_now_reached_this_limi_ELL }
            ];
          }
          return [
            { ...TXTWCL_SorryX_your_deposit_request_ha_ELL },
            {
              ...setLimitsMessage(remainingLimitDollarAmount, daysLimitHitType),
              values: {
                0: (
                  <CurrencyAmount
                    value={getLimits(maxLimitData, daysLimitHitType)}
                    isZeroExponentCurrency={isBalanceCurrencyZeroExponent}
                    currency={balanceCurrency}
                    dollarValue
                  />
                )
              }
            },
            {
              ...TXTWCL_You_can_only_deposit_another_N_ELL,
              values: {
                0: (
                  <CurrencyAmount
                    value={remainingLimitDollarAmount}
                    isZeroExponentCurrency={isBalanceCurrencyZeroExponent}
                    currency={balanceCurrency}
                    dollarValue
                  />
                )
              }
            }
          ];
        }
        if (depositLimitHitType === DepositLimitHitTypes.UkSpendBudgetLimit) {
          if (remainingLimitDollarAmount !== 0) {
            return [
              {
                values: {
                  0: maxLimitWithCurrency
                },
                ...TXTWCL_You_have_N0_remaining_of_your__ELL
              },
              { ...TXTWCL__Please_enter_a_lower_amountX },
              { ...TXTWCL_Visit_our_Help_Center_to_find__ELL }
            ];
          } else {
            return [
              {
                values: {
                  0: maxLimitWithCurrency
                },
                ...TXTWCL_You_have_used_all_your_Spend_B_ELL
              },
              { ...TXTWCL_Your_Spend_Budget_resets_on_th_ELL },

              {
                ...TXTWCL_To_find_out_moreX_please_visit_ELL,
                values: {
                  a: (msg: React.ReactNode) => (
                    <ResolverLink resolverTag={ResolverTag.HelpCenterSpendBudgetArticle}>
                      {msg}
                    </ResolverLink>
                  )
                }
              }
            ];
          }
        }
        if (
          depositLimitHitType === DepositLimitHitTypes.SpainDepositLimit &&
          remainingLimitDollarAmount === 0
        ) {
          return { ...TXTWCL_You_have_reached_spain };
        }
        return {
          ...depositLimitMessages[depositLimitHitType],
          values: {
            0: maxLimitWithCurrency
          }
        };
      }

      return [
        {
          ...TXTWCL_There_is_a_limit_of_N0_for_thi_ELL,
          values: {
            0: (
              <CurrencyAmount
                value={transaction}
                isZeroExponentCurrency={isBalanceCurrencyZeroExponent}
                currency={balanceCurrency}
                dollarValue
              />
            )
          }
        }
      ];
    };

    return { valid: valid(), message: message() };
  };
